import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { Deposit } from "../../../../../api/deposits";
import { useBills } from "../../../../../hooks/useBills";
import {
  useAttachBillDeposit,
  useDetachBillDeposit,
} from "../../../../../hooks/useDeposits";
import { getApiErrorMessage } from "../../../../../utils/translations";
import { useOrderingMessage } from "../../useOrderingMessage";
import { attachedDepositOf } from "./billDeposit";
import { DepositPickerModal } from "./DepositPickerModal";

/** Масата, за която е отворен изборът — от момента на натискането, не
 *  избраната по-късно (сервитьорът може да кликне друга маса отзад). */
interface DepositPickerTarget {
  billId: number;
  tableName: string;
}

/**
 * „Използвай депозит": изборът (`DepositPickerModal`) за една маса и двете
 * заявки — закачане (`PUT /bills/{id}/deposit`) и „Махни депозита"
 * (`DELETE`). Закаченият се чете от свежия списък сметки, за да вижда
 * смяната от друг терминал. Отказът на BE (депозитът вече е на друга маса,
 * изтекъл, без бон, сметката е закрита) е червено известие, изборът остава.
 */
export function useBillDepositPicker() {
  const { t } = useTranslation();
  const message = useOrderingMessage();
  const [target, setTarget] = useState<DepositPickerTarget | null>(null);
  const { data: bills = [] } = useBills({ fromCacheOnly: true });
  const attachMutation = useAttachBillDeposit();
  const detachMutation = useDetachBillDeposit();
  const busy = attachMutation.isPending || detachMutation.isPending;

  const bill = target ? bills.find((candidate) => candidate.id === target.billId) : undefined;
  const attached = attachedDepositOf(bill);

  const close = () => setTarget(null);

  const attach = async (deposit: Deposit) => {
    if (!target || busy) return;
    if (deposit.id === attached?.id) {
      close();
      return;
    }
    try {
      await attachMutation.mutateAsync({ billId: target.billId, depositId: deposit.id });
      message.success(
        t("depositAction.attached", {
          id: deposit.id,
          name: deposit.name,
          table: target.tableName,
        }),
      );
      close();
    } catch (error) {
      message.error(getApiErrorMessage(error, t("depositAction.attachError")));
    }
  };

  const remove = async () => {
    if (!target || busy || !attached) return;
    try {
      await detachMutation.mutateAsync(target.billId);
      message.success(
        t("depositAction.removed", { id: attached.id, table: target.tableName }),
      );
      close();
    } catch (error) {
      message.error(getApiErrorMessage(error, t("depositAction.removeError")));
    }
  };

  const modal = target ? (
    <DepositPickerModal
      open
      billId={target.billId}
      tableName={target.tableName}
      attachedId={attached?.id ?? null}
      onPick={(deposit) => void attach(deposit)}
      onRemove={() => void remove()}
      busy={busy}
      onClose={close}
    />
  ) : null;

  return { open: target !== null, openFor: setTarget, modal };
}
